import React, { useCallback, useMemo } from 'react';
import { View, ActionButton, Text } from '@adobe/react-spectrum';
import { Picker, Item } from '@adobe/react-spectrum';
import Close from '@spectrum-icons/workflow/Close';
import { DateComponent } from './DateComponent';
import { FormField } from '../../store/slice';
import styles from './RepeatableGroupComponentField.module.scss';

/** One row object keyed by `fieldGroup[].name`. */
export type RepeatableGroupRow = Record<string, any>;

interface RepeatableGroupComponentFieldProps {
  field: FormField;
  value: RepeatableGroupRow[];
  onChange: (fieldName: string, value: RepeatableGroupRow[]) => void;
  hasSubmitted?: boolean;
  isInvalid?: boolean;
}

const DEFAULT_MAX_ROWS = 4;
const DEFAULT_MIN_ROWS = 1;
const DEFAULT_PRIMARY_SELECT_NAME = 'routeToMarket';
const DEFAULT_DISABLE_NA_FALLBACK_NAME = 'customerNotificationDate';

export const RepeatableGroupComponentField: React.FC<
  RepeatableGroupComponentFieldProps
> = ({ field, value, onChange, hasSubmitted = false, isInvalid = false }) => {
  const groupFields: FormField[] = useMemo(() => {
    return Array.isArray(field.fieldGroup) ? field.fieldGroup : [];
  }, [field.fieldGroup]);

  const primarySelectField =
    groupFields.find(f => f.name === DEFAULT_PRIMARY_SELECT_NAME) ||
    groupFields.find(f => f.type === 'select');
  const primarySelectFieldName =
    primarySelectField?.name || DEFAULT_PRIMARY_SELECT_NAME;
  const primarySelectOptions: Array<{ value: string; label: string }> =
    primarySelectField?.options || [];

  const minimumRows = Math.max(
    DEFAULT_MIN_ROWS,
    Number(field.minItems) || DEFAULT_MIN_ROWS
  );
  const maximumRows = Math.max(
    minimumRows,
    Number(field.maxItems) || DEFAULT_MAX_ROWS
  );

  const createEmptyRow = useCallback((): RepeatableGroupRow => {
    return groupFields.reduce<RepeatableGroupRow>((acc, groupField) => {
      if (groupField.defaultValue !== undefined) {
        acc[groupField.name] = groupField.defaultValue;
      } else if (groupField.type === 'select') {
        acc[groupField.name] = '';
      } else {
        acc[groupField.name] = undefined;
      }
      return acc;
    }, {});
  }, [groupFields]);

  const rows: RepeatableGroupRow[] = useMemo(() => {
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }

    return Array.from({ length: minimumRows }, () => createEmptyRow());
  }, [value, minimumRows, createEmptyRow]);

  const usedPrimaryValues = useMemo(
    () =>
      new Set(
        rows.map(r => r?.[primarySelectFieldName]).filter(Boolean) as string[]
      ),
    [rows, primarySelectFieldName]
  );

  const getAvailablePrimaryOptions = useCallback(
    (currentValue: string) =>
      primarySelectOptions.filter(
        opt => opt.value === currentValue || !usedPrimaryValues.has(opt.value)
      ),
    [primarySelectOptions, usedPrimaryValues]
  );

  const updateRow = useCallback(
    (index: number, subFieldName: string, newValue: any) => {
      const updated = rows.map((row, i) =>
        i === index ? { ...row, [subFieldName]: newValue } : row
      );
      onChange(field.name, updated);
    },
    [rows, onChange, field.name]
  );

  const addRow = useCallback(() => {
    if (rows.length >= maximumRows) return;
    onChange(field.name, [...rows, createEmptyRow()]);
  }, [rows, onChange, field.name, maximumRows, createEmptyRow]);

  const removeRow = useCallback(
    (index: number) => {
      if (rows.length <= minimumRows) return;
      onChange(
        field.name,
        rows.filter((_, i) => i !== index)
      );
    },
    [rows, onChange, field.name, minimumRows]
  );

  const isRowMissingPrimarySelect = (row: RepeatableGroupRow): boolean => {
    if (!hasSubmitted) return false;
    return !row?.[primarySelectFieldName];
  };

  const isDateEmpty = (dateVal: any): boolean => {
    if (!dateVal) return true;
    if (typeof dateVal === 'string') return dateVal.trim() === '';
    if (typeof dateVal === 'object') {
      if (dateVal.type === 'special' && dateVal.value === 'N/A') return true;
      if (dateVal.type === 'date' && !dateVal.date) return true;
      if (
        dateVal.type === 'months' &&
        (!dateVal.months || dateVal.months.length === 0)
      )
        return true;
      if (
        dateVal.type === 'quarters' &&
        (!dateVal.quarters || dateVal.quarters.length === 0)
      )
        return true;
    }
    return false;
  };

  if (groupFields.length === 0) {
    return null;
  }

  return (
    <View UNSAFE_className={styles.repeatableGroupContainer}>
      {rows.map((row, index) => {
        return (
          <View
            key={`repeatable-group-row-${index}`}
            UNSAFE_className={`${styles.phaseRow} ${
              isRowMissingPrimarySelect(row) ? styles.phaseRowError : ''
            }`}
            data-testid={`repeatable-group-row-${index}`}
          >
            <View UNSAFE_className={styles.phaseFields}>
              {groupFields.map((groupField, groupFieldIndex) => {
                const subValue = row?.[groupField.name];
                const subFieldInvalid =
                  hasSubmitted &&
                  groupField.required &&
                  (groupField.type === 'datepicker'
                    ? isDateEmpty(subValue)
                    : subValue === undefined ||
                      subValue === null ||
                      (typeof subValue === 'string' && subValue.trim() === ''));

                const fieldContainerClass = `${styles.phaseField} ${
                  subFieldInvalid ? styles.fieldError : ''
                }`;
                const isLastField = groupFieldIndex === groupFields.length - 1;

                const renderedField =
                  groupField.type === 'select' ? (
                    <>
                      <label className={styles.fieldLabel}>
                        {groupField.label}
                        {groupField.required && (
                          <span className={styles.required}>*</span>
                        )}
                      </label>
                      <Picker
                        aria-label={groupField.label || groupField.name}
                        selectedKey={
                          typeof subValue === 'string' && subValue !== ''
                            ? subValue
                            : null
                        }
                        onSelectionChange={key =>
                          updateRow(index, groupField.name, key as string)
                        }
                        width="100%"
                        placeholder={
                          groupField.placeholder ||
                          (groupField.name === primarySelectFieldName
                            ? 'Select RTM'
                            : 'Select')
                        }
                        validationState={
                          subFieldInvalid ? 'invalid' : undefined
                        }
                      >
                        {(groupField.name === primarySelectFieldName
                          ? getAvailablePrimaryOptions(
                              typeof subValue === 'string' ? subValue : ''
                            )
                          : groupField.options || []
                        ).map(option => (
                          <Item key={option.value}>{option.label}</Item>
                        ))}
                      </Picker>
                    </>
                  ) : groupField.type === 'datepicker' ? (
                    <DateComponent
                      field={{
                        ...groupField,
                        id: `repeatableRow_${index}_${
                          groupField.id || groupField.name
                        }`,
                        name: `repeatableRow_${index}_${groupField.name}`
                      }}
                      value={subValue}
                      onChange={(_fieldName: string, newValue: any) =>
                        updateRow(index, groupField.name, newValue)
                      }
                      disableNA={
                        (groupField as any).disableNA !== undefined
                          ? Boolean((groupField as any).disableNA)
                          : groupField.name === DEFAULT_DISABLE_NA_FALLBACK_NAME
                      }
                      validationState={subFieldInvalid ? 'invalid' : 'valid'}
                    />
                  ) : (
                    <Text>
                      Unsupported repeatable field type: {groupField.type}
                    </Text>
                  );

                if (isLastField) {
                  return (
                    <View
                      key={`${groupField.name}-${index}`}
                      UNSAFE_className={styles.phaseFieldWithRemove}
                    >
                      <View UNSAFE_className={fieldContainerClass} flex>
                        {renderedField}
                      </View>
                      {rows.length > minimumRows && (
                        <ActionButton
                          isQuiet
                          onPress={() => removeRow(index)}
                          aria-label={`Remove phase ${index + 1}`}
                          UNSAFE_className={styles.removeButton}
                        >
                          <Close size="S" />
                        </ActionButton>
                      )}
                    </View>
                  );
                }

                return (
                  <View
                    key={`${groupField.name}-${index}`}
                    UNSAFE_className={fieldContainerClass}
                  >
                    {renderedField}
                  </View>
                );
              })}
            </View>
          </View>
        );
      })}

      {rows.length < maximumRows && (
        <button
          type="button"
          className={styles.addPhaseButton}
          onClick={addRow}
        >
          <span>Add Phase</span>
          <svg
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.addIcon}
            aria-hidden="true"
          >
            <g clipPath="url(#clip0_4528_83)">
              <path
                d="M6.625 3.07812H4.92188V1.375C4.92188 0.866209 4.50879 0.453119 4.00001 0.453119C3.49121 0.453119 3.07812 0.866209 3.07812 1.375V3.07812H1.37501C0.866215 3.07812 0.453125 3.49121 0.453125 4C0.453125 4.50879 0.866215 4.92188 1.37501 4.92188H3.07812V6.625C3.07812 7.13379 3.49121 7.54688 4.00001 7.54688C4.50879 7.54688 4.92188 7.13379 4.92188 6.625V4.92188H6.625C7.13379 4.92188 7.54688 4.50879 7.54688 4C7.54688 3.49121 7.13379 3.07812 6.625 3.07812Z"
                fill="currentColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_4528_83">
                <rect width="8" height="8" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
      )}

      {hasSubmitted &&
        isInvalid &&
        !rows.some(row => Boolean(row?.[primarySelectFieldName])) && (
          <Text UNSAFE_className={styles.sectionError}>
            At least one route-to-market phase is required
          </Text>
        )}
    </View>
  );
};
